import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/hooks/useApi';
import { MONEY_QUERY_KEYS } from '@/hooks/useInvalidateMoney';
import type {
  OutreachFollowerBracket,
  OutreachInvite,
  InvitePost,
  MessageQuotaReport,
  OutreachInviter,
  OutreachNiche,
  OutreachNicheWithUsage,
  OutreachPayout,
  OutreachPayoutDetail,
  OutreachRate,
  PayrollResponse,
  PayrollBreakdown,
  ObligationAdjustment,
  PayoutProviderKey,
  PayoutStatus,
  CreatorRequest,
  CreatorRequestComment,
  CreatorRequestDetail,
  CreatorRequestRequester,
  CreatorRequestStatus,
  CreatorRequestCancellationReason,
  OutreachAccessRequest,
  AccessRequestStatus,
  CronRunsResponse,
  CronRunTriggerAcceptance,
  OutreachChallenge,
  ChallengeStanding,
  ChallengeMetric,
  ContactedNotReplied,
  InviterAnalyticsDetail,
  AttributionDiagnostic,
  TeamPipelineRow,
} from '@/api/types';

const KEYS = {
  inviters: ['admin', 'inviters'] as const,
  invites: ['admin', 'invites'] as const,
  rates: ['admin', 'rates'] as const,
  brackets: ['admin', 'brackets'] as const,
  niches: ['admin', 'niches'] as const,
  payroll: ['admin', 'payroll'] as const,
  payouts: ['admin', 'payouts'] as const,
  providers: ['admin', 'providers'] as const,
  teamSettings: ['admin', 'team-settings'] as const,
  teams: ['admin', 'teams'] as const,
  categories: ['admin', 'categories'] as const,
  creatorRequests: ['admin', 'creator-requests'] as const,
  accessRequests: ['admin', 'access-requests'] as const,
  cronRuns: ['admin', 'cron-runs'] as const,
  discoveryFolders: ['admin', 'discovery-folders'] as const,
  challenges: ['admin', 'challenges'] as const,
  paymentEvents: ['admin', 'payment-events'] as const,
};

export type RequiredInviterField =
  | 'clippingId'
  | 'paypalEmail'
  | 'email'
  | 'inviterPublicId'
  | 'name';

export interface HealthSnapshot {
  ok: boolean;
  checkedAt: string;
  latencyMs?: number;
  detail?: string;
  monitorStartedAt: string;
}

export interface PayoutProviderInfo {
  key: PayoutProviderKey;
  displayName: string;
  requiredInviterFields: RequiredInviterField[];
  attributionNote: string | null;
  capabilities: { callbacks: boolean; syncStatus: boolean; cancel: boolean };
  health: HealthSnapshot | null;
  // Legacy mirrors of capabilities — kept for now to avoid breaking AdminProviders
  // mid-migration; will be deleted once the page reads .capabilities.
  supportsCallbacks: boolean;
  supportsSyncStatus: boolean;
  supportsCancel: boolean;
}

export interface VerificationProviderInfo {
  key: 'clipping' | 'rapidapi';
  displayName: string;
  channels: InviteChannel[];
  capabilities: ('IDENTITY' | 'CLIP_ACTIVITY' | 'SOCIAL_FOLLOWERS')[];
  sourceNote: string | null;
  health: HealthSnapshot | null;
}

export interface PayoutProvidersResponse {
  providers: PayoutProviderInfo[];
  verificationProviders: VerificationProviderInfo[];
  routing: {
    default: PayoutProviderKey;
    byTeam: Record<string, PayoutProviderKey>;
  };
}

export function useAdminPayoutProviders() {
  const api = useApi();
  return useQuery({
    queryKey: KEYS.providers,
    queryFn: () => api.get<PayoutProvidersResponse>('/admin/payout-providers'),
    // Backend pings upstreams every 60s; refresh the snapshot at the same
    // cadence so the dashboard stays roughly fresh without hammering.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Manual "check now" mutation. Hits POST /admin/payout-providers/check —
 * the backend runs every provider's healthCheck immediately and returns
 * the refreshed snapshot, which we seed straight into the query cache so
 * the UI updates without a second round-trip.
 */
export function useCheckProvidersNow() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<PayoutProvidersResponse>('/admin/payout-providers/check'),
    onSuccess: (data) => {
      qc.setQueryData(KEYS.providers, data);
    },
  });
}

// ── Team settings ────────────────────────────────────────────────────────

/**
 * Loose alias — teams are CRUDable, so the canonical list is the DB. Use
 * `useAdminTeams()` to enumerate available codes.
 */
export type InviterTeam = string;

export interface AdminTeamSettings {
  id: number;
  team: InviterTeam;
  screenshotRequired: boolean;
  multipleNichesAllowed: boolean;
  /**
   * Whether the morning verification cron resolves this team's invites
   * against the clipping endpoint. Defaults to false — teams opt IN.
   * See spade-outreach-backend's inviteRepo.listUnresolved for the filter.
   */
  clippingResolveRequired: boolean;
  reOutreachCooldownDays: number | null;
  /** Attribution window grace, in hours. An invite is only credited for a join/post
   *  on/after createdAt − this many hours. Default 24. */
  attributionBufferHours: number;
  /**
   * When true, outreachers on this team can soft-delete their own invites
   * (SPA-618). Admin row action bypasses the gate.
   */
  canDeleteInvites: boolean;
  /** Team default for bulk import. Outreachers can be overridden one by one. */
  bulkImportEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export type TeamSettingsPatch = Partial<
  Pick<
    AdminTeamSettings,
    | 'screenshotRequired'
    | 'multipleNichesAllowed'
    | 'clippingResolveRequired'
    | 'canDeleteInvites'
    | 'bulkImportEnabled'
    | 'reOutreachCooldownDays'
    | 'attributionBufferHours'
  >
>;

export function useAdminTeamSettings() {
  const api = useApi();
  return useQuery({
    queryKey: KEYS.teamSettings,
    queryFn: () => api.get<AdminTeamSettings[]>('/admin/team-settings'),
  });
}

export function useUpdateTeamSettings() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      team,
      body,
    }: {
      team: InviterTeam;
      body: TeamSettingsPatch;
    }) => api.patch<AdminTeamSettings>(`/admin/team-settings/${team}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.teamSettings }),
  });
}

// ── Inviters ─────────────────────────────────────────────────────────────
export function useAdminInviters(params?: Record<string, string | number | boolean>) {
  const api = useApi();
  return useQuery({
    queryKey: [...KEYS.inviters, params],
    queryFn: () => api.get<OutreachInviter[]>('/admin/inviters', params),
  });
}

export function useCreateInviter() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<OutreachInviter>) => api.post<OutreachInviter>('/admin/inviters', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.inviters }),
  });
}

export function useUpdateInviter() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<OutreachInviter> }) =>
      api.patch<OutreachInviter>(`/admin/inviters/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.inviters }),
  });
}

/** Weekly messages against each outreacher's quota, newest week last. */
export function useMessageQuotas(params?: Record<string, string | number | boolean>) {
  const api = useApi();
  return useQuery({
    queryKey: [...KEYS.inviters, 'message-quotas', params],
    queryFn: () => api.get<MessageQuotaReport>('/admin/inviters/message-quotas', params),
  });
}

/** Set (or clear, with null) one outreacher's weekly message quota. */
export function useSetMessageQuota() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, quota }: { id: number; quota: number | null }) =>
      api.patch<OutreachInviter>(`/admin/inviters/${id}`, { weeklyMessageQuota: quota }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.inviters }),
  });
}

export function useDeactivateInviter() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del<OutreachInviter>(`/admin/inviters/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.inviters }),
  });
}

export function useResetPortalLink() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<OutreachInviter>(`/admin/inviters/${id}/reset-portal-link`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.inviters }),
  });
}

// ── Invites ──────────────────────────────────────────────────────────────
export function useAdminInvites(
  params?: Record<string, string | number | boolean>,
  options?: { enabled?: boolean },
) {
  const api = useApi();
  return useQuery({
    queryKey: [...KEYS.invites, params],
    queryFn: () => api.getPaged<OutreachInvite[]>('/admin/invites', params),
    enabled: options?.enabled ?? true,
  });
}

/**
 * Creators anyone has DM'd where nobody has logged a reply.
 *
 * Same endpoint as the invites list, under the CONTACTED pseudo-status — but
 * a different row shape, since there is no invite behind these. `enabled`
 * gates it so the query only runs when the view is actually showing them.
 */
export function useAdminContacted(
  params: Record<string, string | number | boolean>,
  enabled: boolean,
) {
  const api = useApi();
  return useQuery({
    queryKey: [...KEYS.invites, 'contacted', params],
    queryFn: () =>
      api.getPaged<ContactedNotReplied[]>('/admin/invites', {
        ...params,
        status: 'CONTACTED',
      }),
    enabled,
  });
}

/**
 * Response includes the attribution window bounds so the InviteDetailPanel can
 * render "Window: <start> → now (buffer Xh)" without recomputing the buffer
 * on the client. `posts[i].inWindow` / `filteredReason` are stamped by the
 * backend using the exact same math as the conversion writer.
 */
export interface InvitePostsResponse {
  posts: InvitePost[];
  unavailable?: boolean;
  bufferHours: number;
  windowStart: string;
}

export function useInvitePosts(inviteId: number | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['admin', 'invite-posts', inviteId],
    queryFn: () => api.get<InvitePostsResponse>(`/admin/invites/${inviteId}/posts`),
    enabled: inviteId !== null,
    staleTime: 60_000,
  });
}

/**
 * Server caps the input array at 500. We clamp client-side too so a future
 * PAGE_SIZE bump (or a different consumer) can't silently send a 400-throwing
 * request. The query key uses the clamped+sorted IDs so cache lookups stay
 * stable across re-renders. Callers can pass `enabled: false` (or a falsy
 * boolean) to suppress the request while a parent query is still fetching,
 * which avoids stacking in-flight POSTs on filter/page changes.
 */
const INVITE_POSTS_BATCH_MAX = 500;

export function useInvitePostsBatch(
  matchedUserIds: string[],
  opts: { enabled?: boolean } = {},
) {
  const api = useApi();
  const ids = matchedUserIds.slice(0, INVITE_POSTS_BATCH_MAX);
  const sortedIds = ids.slice().sort();
  return useQuery({
    queryKey: ['admin', 'invite-posts-batch', ...sortedIds],
    queryFn: () =>
      api.post<{ byMatchedUserId: Record<string, InvitePost[]>; unavailable?: boolean }>(
        '/admin/invites/batch-posts',
        { matchedUserIds: ids },
      ),
    enabled: (opts.enabled ?? true) && ids.length > 0,
    staleTime: 60_000,
  });
}

export function useDiscoveryFolders() {
  const api = useApi();
  return useQuery({
    queryKey: KEYS.discoveryFolders,
    queryFn: () => api.get<string[]>('/admin/invites/discovery-folders'),
  });
}

export function useUpdateInvite() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<OutreachInvite> }) =>
      api.patch<OutreachInvite>(`/admin/invites/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.invites }),
  });
}

export function useReassignInvite() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      inviteId,
      inviterId,
      reason,
    }: {
      inviteId: number;
      inviterId: number;
      reason?: string;
    }) =>
      api.post<OutreachInvite>(`/admin/invites/${inviteId}/reassign`, {
        inviterId,
        ...(reason ? { reason } : {}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.invites });
      qc.invalidateQueries({ queryKey: KEYS.payroll });
    },
  });
}

/**
 * Sole admin path for a signed per-invite adjustment. Backend emits an
 * ADJUSTMENT event via paymentLedger.emit; obligation_adjustments row is
 * also written (double-write during the read-path migration). Renamed
 * from useAdjustInviteOwed → useAdjustInvite when the /adjust-owed
 * route was consolidated to /adjust.
 */
export function useAdjustInvite() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      inviteId,
      amount,
      reason,
    }: {
      inviteId: number;
      amount: string;
      reason: string;
    }) =>
      api.post<ObligationAdjustment>(`/admin/invites/${inviteId}/adjust`, { amount, reason }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.invites });
      qc.invalidateQueries({ queryKey: KEYS.payroll });
      qc.invalidateQueries({ queryKey: [...KEYS.paymentEvents, vars.inviteId] });
    },
  });
}

/**
 * SPA-545. Download a CSV of the filtered invites list. Mirrors the same
 * query params as `useAdminInvites` so the on-screen filters carry over.
 * The backend caps output (25k rows) and returns 413 with an
 * EXPORT_TOO_LARGE code — surface the message so admins narrow their
 * filters instead of retrying blindly.
 */
export function useExportInvites() {
  const api = useApi();
  return useMutation({
    mutationFn: async (params: Record<string, string | number | boolean | undefined>) => {
      const { blob, filename } = await api.download('/admin/invites/export', params);
      const url = URL.createObjectURL(blob);
      try {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename ?? `invites-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
      } finally {
        URL.revokeObjectURL(url);
      }
    },
  });
}

/**
 * SPA-618. Soft-delete an invite from the admin side. The row stays in the
 * DB with `deletedAt` stamped; the admin table keeps rendering it with a
 * "Deleted" pill so the outreach history isn't lost. Admin bypasses the
 * per-team `canDeleteInvites` gate.
 */
export function useDeleteInvite() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) =>
      api.del<{ publicId: string; deletedAt: string | null }>(`/admin/invites/${publicId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.invites }),
  });
}

export function useRejectInvite() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ inviteId, reason }: { inviteId: number; reason: string }) =>
      api.post<{ invite: OutreachInvite; netZeroedBy: string }>(
        `/admin/invites/${inviteId}/reject`,
        { reason },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.invites });
      qc.invalidateQueries({ queryKey: KEYS.payroll });
    },
  });
}

export interface ResolveInviteResult {
  invite: OutreachInvite;
  result: {
    matchedDiscordId: string | null;
    externalSocialAccountId: string | null;
    isInServer: boolean;
    hasApprovedClips: boolean;
    approvedClipCount: number;
    followerCount: number | null;
    resolvedAt: string;
  } | null;
  /**
   * Backend-computed explanation of the attribution decision. Null only when
   * the identity provider returned no result at all (rare — usually resolveOne
   * short-circuits to null and there's nothing to explain).
   */
  attribution: AttributionDiagnostic | null;
}

export function useResolveInvite() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inviteId: number) =>
      api.post<ResolveInviteResult>(`/admin/invites/${inviteId}/resolve`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.invites });
    },
  });
}

// ── Rates ────────────────────────────────────────────────────────────────
/**
 * Rate changes move the Niches page too (its bonus column and per-niche rate
 * counts), so every rate mutation refreshes both.
 */
function invalidateRatesAndNiches(qc: ReturnType<typeof useQueryClient>): Promise<unknown> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: KEYS.rates }),
    qc.invalidateQueries({ queryKey: KEYS.niches }),
  ]);
}
export function useAdminRates() {
  const api = useApi();
  return useQuery({ queryKey: KEYS.rates, queryFn: () => api.get<OutreachRate[]>('/admin/rates') });
}
export function useCreateRate() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<OutreachRate>) => api.post<OutreachRate>('/admin/rates', body),
    onSuccess: () => invalidateRatesAndNiches(qc),
  });
}
export function useUpdateRate() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<OutreachRate> }) =>
      api.put<OutreachRate>(`/admin/rates/${id}`, body),
    onSuccess: () => invalidateRatesAndNiches(qc),
  });
}
export function useDeactivateRate() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del<OutreachRate>(`/admin/rates/${id}`),
    onSuccess: () => invalidateRatesAndNiches(qc),
  });
}

// ── Brackets ─────────────────────────────────────────────────────────────
export function useAdminBrackets() {
  const api = useApi();
  return useQuery({
    queryKey: KEYS.brackets,
    queryFn: () => api.get<OutreachFollowerBracket[]>('/admin/follower-brackets'),
  });
}
export function useCreateBracket() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<OutreachFollowerBracket>) =>
      api.post<OutreachFollowerBracket>('/admin/follower-brackets', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.brackets }),
  });
}
export function useUpdateBracket() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<OutreachFollowerBracket> }) =>
      api.put<OutreachFollowerBracket>(`/admin/follower-brackets/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.brackets }),
  });
}
export function useDeactivateBracket() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del<OutreachFollowerBracket>(`/admin/follower-brackets/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.brackets }),
  });
}

// ── Niches ───────────────────────────────────────────────────────────────
export function useAdminNiches() {
  const api = useApi();
  return useQuery({ queryKey: KEYS.niches, queryFn: () => api.get<OutreachNiche[]>('/admin/niches') });
}
export function useCreateNiche() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<OutreachNiche>) => api.post<OutreachNiche>('/admin/niches', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.niches }),
  });
}
export function useUpdateNiche() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<OutreachNiche> }) =>
      api.patch<OutreachNiche>(`/admin/niches/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.niches }),
  });
}
/**
 * Niches with per-niche reference counts (invites, prospects, requests,
 * challenges, rates). Only the Niches page needs these; everything else uses
 * the plain list. Shares the `niches` key prefix, so niche and rate
 * mutations refresh it too.
 */
export function useAdminNichesWithUsage() {
  const api = useApi();
  return useQuery({
    queryKey: [...KEYS.niches, 'usage'],
    queryFn: () => api.get<OutreachNicheWithUsage[]>('/admin/niches', { withUsage: true }),
  });
}
/**
 * Hard delete. The backend refuses (409) while anything still references the
 * niche, so this is only offered for unused ones.
 */
export function useDeleteNiche() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del<null>(`/admin/niches/${id}`),
    onSuccess: () => invalidateRatesAndNiches(qc),
  });
}

// ── Payroll ──────────────────────────────────────────────────────────────
export function useAdminPayroll(periodOf?: string, period: 'weekly' | 'monthly' = 'weekly') {
  const api = useApi();
  return useQuery({
    queryKey: [...KEYS.payroll, period, periodOf],
    queryFn: () =>
      api.get<PayrollResponse>('/admin/payroll', {
        period,
        ...(periodOf ? { periodOf } : {}),
      }),
  });
}

export function useAdminPayrollBreakdown(
  inviterPublicId: string | null,
  periodOf?: string,
  period: 'weekly' | 'monthly' = 'weekly',
) {
  const api = useApi();
  return useQuery({
    queryKey: [...KEYS.payroll, 'breakdown', inviterPublicId, period, periodOf],
    enabled: !!inviterPublicId,
    queryFn: () =>
      api.get<PayrollBreakdown>(
        `/admin/payroll/inviters/${inviterPublicId}/breakdown`,
        { period, ...(periodOf ? { periodOf } : {}) },
      ),
  });
}

// ── Payouts ──────────────────────────────────────────────────────────────
//
// Note: `useAdjustmentsForInvite` / `useCreateAdjustment` were removed when
// the /admin/adjustments route was retired. Per-invite adjustments now go
// through `useAdjustInvite` (POST /admin/invites/:id/adjust); the historical
// event stream lives on `useInvitePaymentHistory` further down.

export interface PayoutsFilters {
  inviterId?: number;
  status?: PayoutStatus;
  providerKey?: PayoutProviderKey;
  page?: number;
  limit?: number;
}

/** One page of payouts plus `meta` (page, limit ≤ 200, total). */
export function useAdminPayouts(filters?: PayoutsFilters) {
  const api = useApi();
  return useQuery({
    queryKey: [...KEYS.payouts, filters],
    queryFn: () =>
      api.getPaged<OutreachPayout[]>(
        '/admin/payouts',
        filters as Record<string, string | number | boolean>,
      ),
    placeholderData: (prev) => prev,
  });
}

/** Detail fetch — line items carry `invitePublicId` for the ledger drill-down. */
export function useAdminPayoutDetail(publicId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: [...KEYS.payouts, 'detail', publicId],
    enabled: !!publicId,
    queryFn: () => api.get<OutreachPayoutDetail>(`/admin/payouts/${publicId}`),
  });
}

export function useCreatePayout() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      inviterPublicId,
      body,
    }: {
      inviterPublicId: string;
      body: {
        weekOf: string;
        additionalAmount?: string;
        notes?: string | null;
        /** Pay below owed: cash to send. Requires settleRemainder when < owed. */
        payAmount?: string;
        /** Confirm writing off the unpaid remainder via SETTLEMENT events. */
        settleRemainder?: boolean;
      };
    }) =>
      api.post<OutreachPayout>(
        `/admin/payroll/inviters/${inviterPublicId}/pay`,
        body,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.payouts });
      qc.invalidateQueries({ queryKey: KEYS.payroll });
    },
  });
}

export interface ManualPayoutLineItemInput {
  inviteId: number;
  amount: string;
}

export interface CreateManualPayoutBody {
  inviterId: number;
  lineItems: ManualPayoutLineItemInput[];
  paidVia?: string;
  externalReceiptId?: string;
  paidAt?: string;
  notes?: string;
}

/**
 * Create a payout in PAID state without going through a provider. Used
 * to reconcile off-platform payments (e.g. the operator paid via the
 * clipping dashboard at an adjusted amount and needs the invite ledger
 * to reflect that). For invites where nothing was paid, don't include a
 * line item — use `useAdjustInvite` to write off the owed value instead.
 */
export function useCreateManualPayout() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateManualPayoutBody) =>
      api.post<OutreachPayout>('/admin/payouts/manual', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.payouts });
      qc.invalidateQueries({ queryKey: KEYS.payroll });
      qc.invalidateQueries({ queryKey: KEYS.invites });
      qc.invalidateQueries({ queryKey: KEYS.paymentEvents });
    },
  });
}

export function useResubmitPayout() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) =>
      api.post<OutreachPayout>(`/admin/payouts/${publicId}/resubmit`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.payouts });
      qc.invalidateQueries({ queryKey: KEYS.payroll });
    },
  });
}

export interface PayoutSyncStatus {
  payoutId: number;
  providerKey: PayoutProviderKey;
  providerExternalId: string | null;
  localStatus: PayoutStatus;
  synced: boolean;
  provider?: {
    status: string;
    paidAt?: string;
    paidVia?: string;
    externalReceiptId?: string;
    failureReason?: string;
  };
  reason?: string;
}

export function useCheckPayoutSync() {
  const api = useApi();
  return useMutation({
    mutationFn: (publicId: string) =>
      api.get<PayoutSyncStatus>(`/admin/payouts/${publicId}/sync-status`),
  });
}

// Note: `useReconcilePayoutFromProvider`, `useSettleInvitesForPayout`,
// `useMarkPayoutPaid`, and `useResetInviter` were removed with the bandaid
// consolidation PR. Their operator equivalents now:
//   - "reconcile from clipping" → the sync-from-provider route is kept as
//     an internal capability + cron target; no UI button. Operators use
//     `useCreateManualPayout` (off-platform record) or `useAdjustInvite`
//     (per-invite writeoff) instead.
//   - "settle invites" → `useAdjustInvite` per invite (or a manual payout).
//   - "mark paid" → for a provider row use the sync-from-provider route;
//     for off-platform use `useCreateManualPayout`.
//   - "reset ledger" → loop `useAdjustInvite` over the owed invites; if
//     the bulk case comes back as a real operator need, restore it as a
//     wrapper that emits N ADJUSTMENT events.

export function useCancelPayout() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, reason }: { publicId: string; reason?: string }) =>
      api.post<OutreachPayout>(`/admin/payouts/${publicId}/cancel`, {
        reason: reason ?? '',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.payouts });
      qc.invalidateQueries({ queryKey: KEYS.payroll });
    },
  });
}

// ── Payment history ─────────────────────────────────────────────────────

export type InvitePaymentEventSource =
  | 'PAYOUT'
  | 'RECONCILE'
  | 'ADJUSTMENT'
  | 'REJECT'
  | 'SETTLEMENT'
  | 'IMPORT'
  | 'REVERSAL';

export interface InvitePaymentEventRow {
  id: number;
  publicId: string;
  source: InvitePaymentEventSource;
  signedAmount: string;
  previousPaid: string;
  currentPaid: string;
  reason: string;
  createdBy: string;
  createdAt: string;
  payout: {
    id: number;
    publicId: string;
    providerKey: string;
    paidAt: string | null;
  } | null;
  reversesEventId: number | null;
  reversedByEventId: number | null;
}

export interface InvitePaymentHistoryResponse {
  invite: {
    publicId: string;
    targetIdentifier: string;
    lockedValue: string | null;
    amountPaid: string;
    adjustmentsTotal: string;
  };
  events: InvitePaymentEventRow[];
}

/**
 * Full chronological payment event stream for a single invite. Powers the
 * Payment History tab on the invite detail expansion. Every mutation to
 * `amount_paid` (or adjustment-source events that affect net owed) shows
 * up here in one indexed-lookup response.
 */
export function useInvitePaymentHistory(publicId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: [...KEYS.paymentEvents, publicId],
    enabled: !!publicId,
    queryFn: () =>
      api.get<InvitePaymentHistoryResponse>(`/admin/payment-events/by-invite/${publicId}`),
  });
}

/**
 * Undo — emit a REVERSAL event that negates a prior cash-source event.
 * Backend rejects REVERSAL of ADJUSTMENT / REJECT / IMPORT / already-
 * reversed events with an actionable error. Idempotent: repeated calls
 * on the same original event return the existing reversal via
 * unique-index short-circuit.
 */
export function useReverseEvent() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, reason }: { eventId: number; reason: string }) =>
      api.post<{ event: InvitePaymentEventRow; replayed: boolean }>(
        `/admin/payment-events/${eventId}/reverse`,
        { reason },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.paymentEvents });
      qc.invalidateQueries({ queryKey: KEYS.payouts });
      qc.invalidateQueries({ queryKey: KEYS.payroll });
      qc.invalidateQueries({ queryKey: KEYS.invites });
    },
  });
}

// ── Teams ────────────────────────────────────────────────────────────────

export type InviteChannel = 'DISCORD' | 'TIKTOK' | 'INSTAGRAM' | 'YOUTUBE' | 'TWITTER';
export type PayoutFormula = 'FULL_RATE_CARD' | 'SIMPLE_LOG_BONUS';

export interface OutreachTeam {
  id: number;
  code: string;
  displayName: string;
  /** Many-to-many — a team may recruit invites across multiple categories. */
  inviteCategories: string[];
  allowedChannels: InviteChannel[];
  defaultPayoutProviderKey: PayoutProviderKey | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTeamInput {
  code: string;
  displayName: string;
  inviteCategories: string[];
  allowedChannels: InviteChannel[];
  defaultPayoutProviderKey?: PayoutProviderKey | null;
}

export interface UpdateTeamInput {
  displayName?: string;
  inviteCategories?: string[];
  allowedChannels?: InviteChannel[];
  defaultPayoutProviderKey?: PayoutProviderKey | null;
  isActive?: boolean;
}

export function useAdminTeams() {
  const api = useApi();
  return useQuery({
    queryKey: KEYS.teams,
    queryFn: () => api.get<OutreachTeam[]>('/admin/teams'),
  });
}

export function useCreateTeam() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTeamInput) => api.post<OutreachTeam>('/admin/teams', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.teams });
      qc.invalidateQueries({ queryKey: KEYS.teamSettings });
    },
  });
}

export function useUpdateTeam() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, body }: { code: string; body: UpdateTeamInput }) =>
      api.patch<OutreachTeam>(`/admin/teams/${code}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.teams });
      qc.invalidateQueries({ queryKey: KEYS.teamSettings });
    },
  });
}

export function useDeactivateTeam() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api.del<OutreachTeam>(`/admin/teams/${code}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.teams }),
  });
}

// ── Categories ───────────────────────────────────────────────────────────

export interface OutreachCategory {
  id: number;
  code: string;
  displayName: string;
  payoutFormula: PayoutFormula;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCategoryInput {
  code: string;
  displayName: string;
  payoutFormula?: PayoutFormula;
}

export interface UpdateCategoryInput {
  displayName?: string;
  payoutFormula?: PayoutFormula;
  isActive?: boolean;
}

export function useAdminCategories() {
  const api = useApi();
  return useQuery({
    queryKey: KEYS.categories,
    queryFn: () => api.get<OutreachCategory[]>('/admin/categories'),
  });
}

export function useCreateCategory() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCategoryInput) =>
      api.post<OutreachCategory>('/admin/categories', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.categories }),
  });
}

export function useUpdateCategory() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, body }: { code: string; body: UpdateCategoryInput }) =>
      api.patch<OutreachCategory>(`/admin/categories/${code}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.categories }),
  });
}

export function useDeactivateCategory() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api.del<OutreachCategory>(`/admin/categories/${code}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.categories }),
  });
}

// ── Creator requests ──────────────────────────────────────────────────────

export interface AdminCreatorRequestsParams {
  [key: string]: string | number | boolean | undefined;
  status?: string;        // comma-separated CreatorRequestStatus list
  targetTeam?: string;
  /** Comma list of target teams to leave out (the main view: Influencer, Vyral). */
  excludeTeam?: string;
  niche?: string;
  channel?: string;
  requesterExternalId?: string;
  requesterName?: string;
  page?: number;
  limit?: number;
}

export function useAdminCreatorRequests(params: AdminCreatorRequestsParams = {}) {
  const api = useApi();
  return useQuery({
    queryKey: [...KEYS.creatorRequests, params],
    queryFn: () => api.getPaged<CreatorRequest[]>('/admin/creator-requests', params),
  });
}

/**
 * Distinct list of CMs across every team. Powers the "filter by CM" picker
 * on the admin requests page. Optionally scope by team to narrow the
 * picker when a Team filter is already active.
 */
export function useAdminRequesterDirectory(targetTeam?: string, excludeTeam?: string) {
  const api = useApi();
  return useQuery({
    queryKey: [...KEYS.creatorRequests, 'requesters', targetTeam ?? null, excludeTeam ?? null],
    queryFn: () =>
      api.get<CreatorRequestRequester[]>(
        '/admin/creator-requests/requesters',
        targetTeam ? { targetTeam } : excludeTeam ? { excludeTeam } : undefined,
      ),
    staleTime: 5 * 60 * 1000,
  });
}

export function useAdminCreatorRequest(publicId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: [...KEYS.creatorRequests, publicId],
    enabled: !!publicId,
    queryFn: () => api.get<CreatorRequestDetail>(`/admin/creator-requests/${publicId}`),
  });
}

export interface UpdateCreatorRequestBody {
  status?: CreatorRequestStatus;
  cancellationReason?: CreatorRequestCancellationReason | null;
  cancellationNote?: string | null;
  assignedInviterId?: number | null;
  rosterInviteId?: number | null;
}

export function useUpdateCreatorRequest() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, body }: { publicId: string; body: UpdateCreatorRequestBody }) =>
      api.patch<CreatorRequest>(`/admin/creator-requests/${publicId}`, body),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.creatorRequests });
      qc.invalidateQueries({ queryKey: [...KEYS.creatorRequests, vars.publicId] });
    },
  });
}

export function useAddCreatorRequestComment() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, body }: { publicId: string; body: string }) =>
      api.post<CreatorRequestComment>(`/admin/creator-requests/${publicId}/comments`, {
        body,
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: [...KEYS.creatorRequests, vars.publicId] });
    },
  });
}

// ── Access requests ──────────────────────────────────────────────────────

export function useAdminAccessRequests(status: AccessRequestStatus | 'ALL' = 'PENDING') {
  const api = useApi();
  return useQuery({
    queryKey: [...KEYS.accessRequests, status],
    queryFn: () =>
      api.getPaged<OutreachAccessRequest[]>('/admin/access-requests', {
        ...(status === 'ALL' ? {} : { status }),
        limit: 100,
      }),
  });
}

export interface ApproveAccessRequestBody {
  team: string;
  compensationModel?: 'PER_CONVERSION' | 'RETAINER';
  name?: string;
  email?: string | null;
  paypalEmail?: string | null;
  clippingId?: string | null;
  retainerAmount?: string | null;
  notes?: string | null;
}

export function useApproveAccessRequest() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: ApproveAccessRequestBody }) =>
      api.post<{ request: OutreachAccessRequest; inviter: OutreachInviter }>(
        `/admin/access-requests/${id}/approve`,
        body,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.accessRequests });
      qc.invalidateQueries({ queryKey: KEYS.inviters });
    },
  });
}

export function useRejectAccessRequest() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) =>
      api.post<OutreachAccessRequest>(`/admin/access-requests/${id}/reject`, {
        reason: reason ?? '',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.accessRequests }),
  });
}

export function useLinkAccessRequest() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, inviterId }: { id: number; inviterId: number }) =>
      api.post<{ request: OutreachAccessRequest; inviter: OutreachInviter }>(
        `/admin/access-requests/${id}/link`,
        { inviterId },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.accessRequests });
      qc.invalidateQueries({ queryKey: KEYS.inviters });
    },
  });
}

// ── Cron Runs ────────────────────────────────────────────────────────────
/**
 * Recent cron-run audit log, grouped per job. Powers the drawer at the top
 * of the admin invites panel. Polls every 60s while the drawer is mounted
 * so admins see new runs without a manual refresh.
 */
export function useAdminCronRuns(options?: { perJob?: number; enabled?: boolean; watchJob?: string; watching?: boolean }) {
  const api = useApi();
  const perJob = options?.perJob ?? 10;
  return useQuery({
    queryKey: [...KEYS.cronRuns, perJob],
    queryFn: () => api.get<CronRunsResponse>('/admin/cron-runs', { perJob }),
    // Poll fast while the watched job runs (or the caller expects it to), so
    // the page notices it finishing; otherwise once a minute.
    refetchInterval: (query) => {
      if (options?.watching) return 5_000;
      const g = options?.watchJob ? query.state.data?.groups.find((x) => x.jobName === options.watchJob) : undefined;
      return g?.running ? 5_000 : 60_000;
    },
    enabled: options?.enabled ?? true,
  });
}

/**
 * Fire-and-forget manual trigger for a registered cron job. The mutation
 * resolves as soon as the backend has dispatched the runner (HTTP 202);
 * the audit drawer's poll picks up the eventual OK/FAILED row. Invalidates
 * the list query on success so the new in-flight row shows up quickly.
 */
export function useTriggerCronRun() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (jobName: string) =>
      api.post<CronRunTriggerAcceptance>(`/admin/cron-runs/${jobName}/trigger`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.cronRuns }),
  });
}

// ── Challenges ────────────────────────────────────────────────────────

export function useAdminChallenges() {
  const api = useApi();
  return useQuery({
    queryKey: KEYS.challenges,
    queryFn: () => api.get<OutreachChallenge[]>('/admin/challenges'),
  });
}

export function useAdminChallengeStandings(challengeId: number | null) {
  const api = useApi();
  return useQuery({
    queryKey: [...KEYS.challenges, challengeId, 'standings'],
    queryFn: () =>
      api.get<{ challenge: OutreachChallenge; standings: ChallengeStanding[] }>(
        `/admin/challenges/${challengeId}/standings`,
      ),
    enabled: challengeId !== null,
  });
}

export function useCreateChallenge() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      name: string;
      description?: string;
      metric: ChallengeMetric;
      targetTeam?: string | null;
      targetNiche?: string | null;
      startDate: string;
      endDate: string;
      prizeAmount?: string | null;
      prizeDescription?: string | null;
    }) => api.post<OutreachChallenge>('/admin/challenges', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.challenges }),
  });
}

export function useUpdateChallenge() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: number; isActive?: boolean; [key: string]: unknown }) =>
      api.patch<OutreachChallenge>(`/admin/challenges/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.challenges }),
  });
}

export function useDeleteChallenge() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del(`/admin/challenges/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.challenges }),
  });
}

/**
 * Approve an ended challenge's pending prize: pays it through the prize
 * payout path. `inviterId` picks the winner and is required when first
 * place is tied. Paying changes payroll, payouts and analytics too.
 */
export function useApproveChallengePrize() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, inviterId }: { publicId: string; inviterId?: number }) =>
      api.post<OutreachChallenge>(
        `/admin/challenges/${publicId}/prize/approve`,
        inviterId != null ? { inviterId } : {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.challenges });
      for (const key of MONEY_QUERY_KEYS) void qc.invalidateQueries({ queryKey: [...key] });
    },
  });
}

/** Reject an ended challenge's pending prize: nothing is paid. */
export function useRejectChallengePrize() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, reason }: { publicId: string; reason: string }) =>
      api.post<OutreachChallenge>(`/admin/challenges/${publicId}/prize/reject`, { reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.challenges }),
  });
}

// ---- Analytics --------------------------------------------------------------

const ANALYTICS = ['admin', 'analytics'] as const;
type AnalyticsParams = Record<string, string | number | boolean> | undefined;

export function useInviterAnalytics(publicId: string | null, params?: AnalyticsParams) {
  const api = useApi();
  return useQuery({
    queryKey: [...ANALYTICS, 'inviter', publicId, params],
    queryFn: () => api.get<InviterAnalyticsDetail>(`/admin/analytics/inviters/${publicId}`, params),
    enabled: !!publicId,
  });
}

// ── people pages (ui revamp) ──

export interface AdminInvitersQuery {
  [key: string]: string | number | boolean | undefined;
  team?: string;
  /** Comma list of teams to leave out (the main view: Influencer, Vyral). */
  excludeTeam?: string;
  isActive?: boolean;
  search?: string;
  page?: number;
  limit?: number;
}

function cleanParams(params: AdminInvitersQuery): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') out[k] = v;
  }
  return out;
}

/** One server-side page of outreachers, with `meta.total` for pagination. */
export function useAdminInvitersPaged(params: AdminInvitersQuery, options: { enabled?: boolean } = {}) {
  const api = useApi();
  const query = cleanParams(params);
  return useQuery({
    queryKey: [...KEYS.inviters, 'paged', query],
    queryFn: () => api.getPaged<OutreachInviter[]>('/admin/inviters', query),
    placeholderData: (prev) => prev,
    enabled: options.enabled ?? true,
  });
}

/**
 * Every outreacher matching `params`, walking the server's pages (200 per
 * request) instead of trusting the first page. For lookups by id / publicId
 * and pickers that must not silently miss anyone.
 */
export function useAllAdminInviters(params: AdminInvitersQuery = {}, options: { enabled?: boolean } = {}) {
  const api = useApi();
  const query = cleanParams(params);
  return useQuery({
    queryKey: [...KEYS.inviters, 'all', query],
    enabled: options.enabled ?? true,
    staleTime: 30_000,
    queryFn: async () => {
      const out: OutreachInviter[] = [];
      for (let page = 1; page <= 50; page++) {
        const res = await api.getPaged<OutreachInviter[]>('/admin/inviters', {
          ...query,
          page,
          limit: 200,
        });
        out.push(...res.data);
        if (res.data.length < 200 || out.length >= res.meta.total) break;
      }
      return out;
    },
  });
}

/** Resolve an outreacher from the publicId in a profile URL (active or not). */
export function useAdminInviterByPublicId(publicId: string | null) {
  const all = useAllAdminInviters({}, { enabled: !!publicId });
  const inviter = publicId ? (all.data ?? []).find((i) => i.publicId === publicId) ?? null : null;
  return {
    inviter,
    isLoading: all.isLoading,
    error: all.error as Error | null,
    notFound: !!publicId && all.isSuccess && !inviter,
    refetch: all.refetch,
  };
}

/** Admin attach of an existing invite to a creator request. Needs an assignee on the request. */
export function useAdminAttachInviteToRequest() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, inviteId }: { publicId: string; inviteId: number }) =>
      api.post<{ inviteId: number; invitePublicId: string; attachedCount: number }>(
        `/admin/creator-requests/${publicId}/invites`,
        { inviteId },
      ),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: [...KEYS.creatorRequests, vars.publicId] });
      qc.invalidateQueries({ queryKey: KEYS.creatorRequests });
    },
  });
}

export function useAdminDetachInviteFromRequest() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, inviteId }: { publicId: string; inviteId: number }) =>
      api.del<{ attachedCount: number }>(`/admin/creator-requests/${publicId}/invites/${inviteId}`),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: [...KEYS.creatorRequests, vars.publicId] });
      qc.invalidateQueries({ queryKey: KEYS.creatorRequests });
    },
  });
}

// ── money pages (ui revamp) ──

/**
 * One outreacher by numeric id. For names and profile links where the
 * capped inviters list might not include them.
 */
export function useAdminInviter(id: number | null | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: [...KEYS.inviters, 'by-id', id ?? null],
    enabled: typeof id === 'number' && id > 0,
    queryFn: () => api.get<OutreachInviter>(`/admin/inviters/${id}`),
    staleTime: 5 * 60_000,
  });
}

/**
 * Server-side outreacher search for pickers: matches name or email, so a
 * picker isn't limited to whichever outreachers fit in one capped page.
 * `enabled` lets a closed picker skip the request.
 */
export function useInviterSearch(
  params: { search?: string; team?: string; activeOnly?: boolean; limit?: number },
  enabled = true,
) {
  const api = useApi();
  const query: Record<string, string | number> = { limit: params.limit ?? 25 };
  if (params.search?.trim()) query.search = params.search.trim();
  if (params.team) query.team = params.team;
  if (params.activeOnly) query.isActive = 'true';
  return useQuery({
    queryKey: [...KEYS.inviters, 'search', query],
    enabled,
    queryFn: () => api.get<OutreachInviter[]>('/admin/inviters', query),
    placeholderData: (prev) => prev,
    staleTime: 60_000,
  });
}

// ── home & proofs (ux2) ──

/** Per-team Messaged → Invites → Joined → Posted for one period (ISO bounds). */
export function useTeamPipeline(range: { dateFrom: string; dateTo: string }) {
  const api = useApi();
  return useQuery({
    queryKey: [...ANALYTICS, 'team-pipeline', range],
    queryFn: () => api.get<TeamPipelineRow[]>('/admin/analytics/team-pipeline', range),
    staleTime: 60_000,
  });
}

// ── invites ux2 ──

/** GET /admin/invites/stage-counts: one number per Invites tab and saved view. */
export interface InviteStageCounts {
  /** Every invite that isn't hidden: the All tab. */
  all: number;
  replied: number;
  joined: number;
  posted: number;
  expired: number;
  notPaid: number;
  hidden: number;
  /** Replied (not expired) and logged more than 7 days ago. */
  repliedOver7Days: number;
  loggedLast7Days: number;
}

/**
 * Counts for the Invites page's stage tabs and saved views, under the same
 * filters as the list minus the stage. Keyed under the invites key so every
 * invite mutation (and useInvalidateMoney) refreshes them too.
 */
export function useInviteStageCounts(params: Record<string, string>, options?: { enabled?: boolean }) {
  const api = useApi();
  return useQuery({
    queryKey: [...KEYS.invites, 'stage-counts', params],
    queryFn: () => api.get<InviteStageCounts>('/admin/invites/stage-counts', params),
    enabled: options?.enabled ?? true,
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });
}
